import { memo } from 'react';
import { mergeProps, useFocusRing } from 'react-aria';

import { Glyph } from '../common/Glyph';
import { HomeyIcon } from '../common/HomeyIcon';
import { Icon } from '../common/Icon';
import { DeviceIcon } from '../device/DeviceIcon';
import { useOptionContext } from '../list-box/ListBox';

import { theme } from '../../theme/theme';
import styles from './CommandPaletteItem.module.css';

export const MemoizedCommandPaletteItem = memo(function CommandPaletteItem(props) {
  const { ref, option, item, value } = useOptionContext();
  const focusRing = useFocusRing();

  const command = value.command;
  const canKeepOpenWithShiftEnter =
    command?.keepOpenOnShiftEnter === true || command?.meta?.keepOpenOnShiftEnter === true;
  const canEnterContextWithTab = command.meta?.canEnterContextWithTab === true;
  const appIconObj = command.meta?.appIconObj ?? null;
  const appIconBackgroundColor = command.meta?.appIconBackgroundColor ?? null;
  const iconBackgroundColor = command.meta?.iconBackgroundColor ?? null;
  const imageUrl = command.meta?.imageUrl ?? null;
  const isDeviceEntity = command.meta?.entityType === 'device';
  const hasDeviceIconMeta =
    command.meta?.deviceIconObj != null ||
    command.meta?.deviceIconUrl != null ||
    command.meta?.deviceIconOverride != null;
  const isSelectedState =
    option.isSelected === true || option.isFocused === true || props.isHighlighted === true;
  const iconColor =
    isSelectedState === true ? theme.color.selected_border : theme.color.icon_medium;
  const appIconColor = theme.color.white;
  const appIconBgColor =
    isSelectedState === true ? theme.color.selected_border : appIconBackgroundColor;
  const colorIconBgColor =
    isSelectedState === true ? theme.color.selected_border : iconBackgroundColor;

  const itemProps = mergeProps(option.optionProps, focusRing.focusProps, {
    // The command palette is input-driven: Arrow keys move results and Enter executes them.
    // Keep options out of the normal Tab sequence so Tab can stay reserved for palette actions.
    tabIndex: -1,
    onFocus() {
      props.onHighlight(item.key);
    },
    onClick(event) {
      if (command.isDisabled === true) {
        return;
      }

      if (event.button != null && event.button !== 0) {
        return;
      }

      props.onPress(command);
    },
    onKeyDown(event) {
      if (event.key === 'Enter') {
        if (command.isDisabled === true) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        props.onPress(command, {
          keepOpen: event.shiftKey === true && event.metaKey !== true && event.ctrlKey !== true,
          openInNewTab: event.metaKey === true || event.ctrlKey === true,
        });
      }
    },
  });

  return (
    <li
      {...itemProps}
      ref={ref}
      className={styles.root}
      style={{
        '--module-app-icon-background': appIconBgColor ?? theme.color.icon_dark,
        '--module-color-icon-background': colorIconBgColor ?? theme.color.icon_dark,
      }}
      data-command-key={item.key}
      data-is-selected={option.isSelected}
      data-is-focused={option.isFocused}
      data-is-disabled={option.isDisabled}
      data-is-highlighted={props.isHighlighted}
      data-is-focus-visible={focusRing.isFocusVisible}
    >
      {appIconObj != null && (
        <div className={styles.itemAppIcon}>
          <HomeyIcon iconObj={appIconObj} size="14px" color={appIconColor} />
        </div>
      )}

      {appIconObj == null && command.icon != null && iconBackgroundColor != null && (
        <div className={styles.itemColorIcon}>
          <Glyph source={command.icon} size="18px" color={theme.color.white} />
        </div>
      )}

      {appIconObj == null && imageUrl != null && (
        <div className={styles.itemImage}>
          <Icon source={imageUrl} size="24px" />
        </div>
      )}

      {appIconObj == null &&
        imageUrl == null &&
        (isDeviceEntity === true || hasDeviceIconMeta === true) && (
          <div className={styles.itemIcon}>
            <DeviceIcon
              iconObj={command.meta?.deviceIconObj ?? null}
              iconUrl={command.meta?.deviceIconUrl ?? null}
              iconOverride={command.meta?.deviceIconOverride ?? null}
              size={theme.icon.size_default}
              color={iconColor}
            />
          </div>
        )}

      {appIconObj == null &&
        imageUrl == null &&
        isDeviceEntity !== true &&
        hasDeviceIconMeta !== true &&
        command.icon != null &&
        iconBackgroundColor == null && (
          <div className={styles.itemIcon}>
            <Glyph source={command.icon} size={theme.icon.size_default} color={iconColor} />
          </div>
        )}

      <div className={styles.itemBody}>
        <div className={styles.itemTop}>
          <div className={styles.itemTitle}>{command.title}</div>
        </div>

        {command.subtitle && <div className={styles.itemSubtitle}>{command.subtitle}</div>}
      </div>

      {(canEnterContextWithTab === true || canKeepOpenWithShiftEnter === true) && (
        <div className={styles.itemHint}>
          {canEnterContextWithTab === true && <kbd className={styles.hintKey}>Tab</kbd>}
          {canKeepOpenWithShiftEnter === true && (
            <kbd className={styles.hintKey}>Shift + Enter</kbd>
          )}
        </div>
      )}
    </li>
  );
});
